import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react'

import {
  Button, ModalContext, Text, Line, Form, SidebarModal,
} from '@domain-group/suit-theme'

import { toggleArray } from '@domain-group/javascript-utils'

import useUser, { useUserForm } from '@hooks/useUser'
import useFlipperKey from '@hooks/useFlipperKey'
import useBrandCustomProfiles from '@hooks/useBrandCustomProfiles'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import { filterNotificationTypes } from '@functions/notification'

import { UserModel } from '@models/user'

import ToggleListItem from '@components/ToggleListItem'

import type { BrandCustomProfileModel } from '@models/types'

import { PermissionItem } from '@models/brandCustomProfile'
import NotificationCategory from './components/NotificationCategory'

const MODAL_KEY = 'ManageNotificationOptionsModal'

function mapPermissionDataToCustomPermissions(
  selectedBrandCustomProfile: BrandCustomProfileModel,
) {
  const { permission_data } = selectedBrandCustomProfile

  if (!permission_data) return {}

  return Object.fromEntries(
    Object.entries(permission_data)
      .sort(([groupA], [groupB]) => groupA.localeCompare(groupB)) // Sort by group name
      .map(([group, perms]) => [
        group,
        (perms as PermissionItem[]).map((perm: PermissionItem) => ({
          editable: true,
          is_enabled: perm.is_enabled,
          key: perm.key,
          title: perm.title,
          user_types: perm.user_types || [],
        })),
      ]),
  )
}

function extractDisabledNotifications(profilePermissions: { [key: string]: any[] }): string[] {
  const disabled: string[] = []

  Object.values(profilePermissions).forEach((permissions) => {
    permissions.forEach((permission) => {
      if (!permission.is_enabled){
        disabled.push(permission.key)
      }
    })
  })

  return disabled
}

interface UpdateOptionsParams {
  disabledNotifications: string[],
  entityState: Partial<UserModel>,
  setEntityState: (arg: Partial<UserModel>) => void,
  updatedKey: string,
}

const updateOptions = (params: UpdateOptionsParams) => {
  const {
    disabledNotifications, entityState, setEntityState, updatedKey,
  } = params

  const { options } = entityState

  const updatedOptions = {
    ...options,
    disabled_notifications: toggleArray(disabledNotifications, updatedKey),
  }

  setEntityState({
    ...entityState,
    // @ts-expect-error TS2322
    options: updatedOptions,
  })
}

export interface ModalCallbacks {
  closeModal: () => void,
  updateUser: (payload: HandleCallbackActionParams) => void,
}

interface ModalProps {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageNotificationOptionsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateUser } = callbacks || {}

  const [selectedBrandCustomProfileId, setSelectedBrandCustomProfileId] = useState<string | null>('system_default')
  const [brandCustomProfilePermissions, setBrandCustomProfilePermissions] = useState<
  { [key: string]: any[] } | null>(null)

  const [brandProfileToggleIsEnabled, setBrandProfileToggleIsEnabled] = useState<boolean>(false)

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] || {}
  const { user: initUser } = modalPayload

  const {
    user,
    callbacks: { updateUser: updateFn },
    updating,
  } = useUser(
    initUser,
    {
      performHttpRequests: true,
      requestOptions: { include_brand_custom_profile_user_profile_ids: true },
    },
  )

  const { actable_type, brand_custom_profile_user_profile_ids, enable_brand_custom_profiles } = user

  const userFormPayload = useUserForm(user, { validateOn: 'change' })

  const { entityState, setEntityState } = userFormPayload
  const { options } = entityState

  const filteredNotificationTypes = filterNotificationTypes(actable_type)
  const disabledNotifications: string[] = options?.disabled_notifications || []

  const updateUserPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: {
      ...entityState,
      enable_brand_custom_profiles: brandProfileToggleIsEnabled,
      brand_custom_profile_params: brandProfileToggleIsEnabled ? {
        notifications: selectedBrandCustomProfileId === 'system_default'
          ? 'system_default' : Number(selectedBrandCustomProfileId),
      } : undefined,
    },
    toastText: 'Notification Options Updated',
  }

  const { isEnabled: agencyMarketingPermissionsEnabled } = useFlipperKey('agency_marketing_permissions')

  const brandCustomProfilesPayload = useBrandCustomProfiles({
    filters: {
      brand_id: user?.brand_id,
      profile_type: 'notifications',
      role_id: user?.role_id,
    },
    performHttpRequests: true,
  })

  const { filteredBrandCustomProfiles } = brandCustomProfilesPayload

  useEffect(() => {
    if (brandProfileToggleIsEnabled){
      const foundProfile = filteredBrandCustomProfiles.find(
        profile => profile.id === Number(selectedBrandCustomProfileId),
      )

      if (foundProfile){
        const mappedPermissions = mapPermissionDataToCustomPermissions(foundProfile)

        setBrandCustomProfilePermissions(mappedPermissions)

        const disabledFromProfile = extractDisabledNotifications(mappedPermissions)
        setEntityState({
          ...entityState,
          options: {
            ...options,
            disabled_notifications: disabledFromProfile,
          },
        })
      }
    } else {
      setBrandCustomProfilePermissions(null)
    }
  }, [filteredBrandCustomProfiles.length, brandProfileToggleIsEnabled, selectedBrandCustomProfileId])

  const initialSelectedProfileId = useMemo(() => {
    if (brand_custom_profile_user_profile_ids?.notifications){
      const savedProfileId = String(brand_custom_profile_user_profile_ids.notifications)

      // Check if the saved profile exists in the available profiles
      const profileExists = filteredBrandCustomProfiles.some(
        profile => String(profile.id) === savedProfileId,
      )

      return profileExists ? savedProfileId : 'system_default'
    }

    return 'system_default'
  }, [brand_custom_profile_user_profile_ids, filteredBrandCustomProfiles])

  useEffect(() => {
    if (initialSelectedProfileId !== 'system_default' && enable_brand_custom_profiles){
      setSelectedBrandCustomProfileId(initialSelectedProfileId)
      setBrandProfileToggleIsEnabled(true)
    }

    if (initialSelectedProfileId === 'system_default' && enable_brand_custom_profiles){
      setSelectedBrandCustomProfileId('system_default')
      setBrandProfileToggleIsEnabled(true)
    }
  }, [enable_brand_custom_profiles, initialSelectedProfileId])

  const isUsingCustomProfile = !!(brandProfileToggleIsEnabled
    && selectedBrandCustomProfileId
    && selectedBrandCustomProfileId !== 'system_default')

  const systemDefaultEnabled = !!(brandProfileToggleIsEnabled && selectedBrandCustomProfileId === 'system_default')

  const handleToggleBrandProfile = useCallback(() => {
    setBrandProfileToggleIsEnabled(!brandProfileToggleIsEnabled)
    if (!brandProfileToggleIsEnabled){
      setSelectedBrandCustomProfileId('system_default')
      setBrandCustomProfilePermissions(null)
      setEntityState({
        ...entityState,
        options: {
          ...options,
          disabled_notifications: [],
        },
      })
    }
  }, [brandProfileToggleIsEnabled, entityState, options, setEntityState])

  const handleProfileSelectChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value
    setSelectedBrandCustomProfileId(selectedValue)

    if (selectedValue === 'system_default'){
      setEntityState({
        ...entityState,
        options: {
          ...options,
          disabled_notifications: [],
        },
      })
      setBrandCustomProfilePermissions(null)
    }
  }, [entityState, options, setEntityState])

  // Determine which notification data source to use based on profile selection
  const getNotificationTypeForCategory = (categoryKey: string) => {
    if (isUsingCustomProfile && brandCustomProfilePermissions){
      return brandCustomProfilePermissions[categoryKey]
    }

    return (filteredNotificationTypes as Record<string, any>)[categoryKey]
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Notification" titleSecondLine="Options" />

      <SidebarModal.Content>
        {agencyMarketingPermissionsEnabled && (
          <>
            <ToggleListItem
              onClick={handleToggleBrandProfile}
              toggleValue={brandProfileToggleIsEnabled}
              toggleOnText="Yes"
              toggleOffText="No"
            >
              <Text
                color="bodyFontLightColor"
                fontSize="small"
                maxWidth="fit-content"
                variant="twoLineEllipsis"
                paddingRight="large"
              >
                Use Brand Profile
              </Text>
            </ToggleListItem>

            {brandProfileToggleIsEnabled && (
              <Form.Field label="Select Brand Profile">
                <select
                  id="brand-profile-select"
                  value={selectedBrandCustomProfileId || 'system_default'}
                  onChange={handleProfileSelectChange}
                >
                  <option value="system_default">System Default</option>
                  {filteredBrandCustomProfiles.map(profile => (
                    <option
                      key={profile.id}
                      value={profile.id}
                    >
                      {profile.title}
                    </option>
                  ))}
                </select>
              </Form.Field>
            )}

            <Line />
          </>
        )}

        {(isUsingCustomProfile && brandCustomProfilePermissions
          ? Object.keys(brandCustomProfilePermissions)
          : Object.keys(filteredNotificationTypes)
        ).map(key => (
          <NotificationCategory
            callbacks={{
              updateNotificationOptions: (updatedKey: string) => {
                // Only allow updates if not using system default
                if (!systemDefaultEnabled){
                  updateOptions({
                    disabledNotifications,
                    entityState,
                    setEntityState,
                    updatedKey,
                  })
                }
              },
            }}
            category={key}
            disabledNotifications={disabledNotifications}
            key={key}
            notificationType={getNotificationTypeForCategory(key)}
            isBrandCustomProfile={isUsingCustomProfile && agencyMarketingPermissionsEnabled}
            systemDefaultEnabled={systemDefaultEnabled}
          />
        ))}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => updateUser(updateUserPayload)}
          size="large"
        >
          Save Options
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageNotificationOptionsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
